import React, {useCallback, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import Dialog from "../../base/dialog";
import Divider from "@mui/material/Divider";
import Button from "../../base/button/button";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {MuiRFF, Yup} from '../../form';
import {OutlinedInputForm} from "../../base/input/outlined_input";
import PhoneInput from "../../base/input/phone-input";
import {OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";
import {deepEqual} from "../../../../core/services/utils/utils";
import Form from "../../base/form";

const schema = Yup.object().shape({
    firstName: Yup.string().trim().nullable().required(ValidateMessages.required),
    lastName: Yup.string().trim().nullable().required(ValidateMessages.required),
    email: Yup.string().email(ValidateMessages.incorrect).nullable().required(ValidateMessages.required),
    phone: Yup.string().trim().nullable()
        .required(ValidateMessages.required)
        .phoneNo(ValidateMessages.incorrectType("phone"), 10),
    membershipName: Yup.string().nullable().required(ValidateMessages.required),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


const UpdateCustomerMemberInformationDialog = ({
                                                   open,
                                                   setOpen,
                                                   member,
                                                   onSuccess,
                                                   statuses,
                                               }) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    const initialValues = useMemo(() => ({
        firstName: member?.firstName,
        lastName: member?.lastName,
        email: member?.email,
        phone: member?.phone,
        membershipName: member?.membershipName,
    }), [member]);

    /**
     * Updates the information of the member.
     */
    const updateInformation = useCallback(async (values) => {
        setSubmitting(true);
        const response = await Api.updateUserProfileInfo({...values, id: member?.id})
        if (!isMounted()) return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onSuccess({...member, ...values});
            setOpen(false);
        }
    }, [isMounted, member, onSuccess, setOpen]);

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'sm'}
        >
            <Row className='no-gutters justify-content-center align-items-start w-100'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className={`text-lg text-color-1 font-weight-semi-bold text-center`}>
                        Update Member Information
                    </p>
                </Row>
                <Divider className='divider'/>
                <Row className='no-gutters justify-content-around w-100'>
                    <Row className='no-gutters justify-content-around w-100 mx-4 my-4'>
                        <Form
                            onSubmit={updateInformation}
                            validate={validate}
                            initialValues={initialValues}
                            render={({values, initialValues, form}) => {
                                const colClass = 'px-2 mb-4';
                                return (
                                    <Row className='no-gutters justify-content-center w-100'>
                                        <Col xs={12} className={colClass}>
                                            <OutlinedInputForm
                                                label={'First Name'}
                                                name={`firstName`}
                                                required={required.firstName}
                                                value={values.firstName ?? ''}
                                            />
                                        </Col>
                                        <Col xs={12} className={colClass}>
                                            <OutlinedInputForm
                                                label={'Last Name'}
                                                name={`lastName`}
                                                required={required.lastName}
                                                value={values.lastName ?? ''}
                                            />
                                        </Col>
                                        <Col xs={12} className={colClass}>
                                            <OutlinedInputForm
                                                label={'Email Address'}
                                                name={`email`}
                                                required={required.email}
                                                value={values.email ?? ''}
                                            />
                                        </Col>
                                        <Col xs={12} className={colClass}>
                                            <PhoneInput
                                                form={form}
                                                label={'Phone Number'}
                                                aria-label={'Phone Number of the user'}
                                                name={'phone'}
                                                required={required.phone}
                                                value={values?.phone ?? ''}
                                            />
                                        </Col>
                                        <Col xs={12} className={`${colClass}`}>
                                            <OutlinedDropdownForm
                                                value={statuses?.find(e => e.name === values?.membershipName)?.name ?? ''}
                                                label={'Status'}
                                                required={required.membershipName}
                                                name={'membershipName'}
                                                data={statuses?.map(e => e.name)?.map(e => ({
                                                    value: e.name,
                                                    label: e,
                                                    id: e,
                                                }) ?? [])}
                                            />
                                        </Col>
                                        <Col xs={12} className={`${colClass}`}>
                                            <Button
                                                disabled={!!submitting || deepEqual(initialValues, values)}
                                                type={'submit'}
                                                color={'primary'}
                                                variant={'contained'}
                                                size={'large'}
                                                fullWidth
                                            >
                                                Update
                                            </Button>
                                        </Col>
                                    </Row>
                                );
                            }}
                        />
                    </Row>
                </Row>
            </Row>
        </Dialog>
    );
}

export default UpdateCustomerMemberInformationDialog;
